import React, { useState, useEffect } from "react";
import { Popover, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useWorkflow } from "../../context/WorkflowContext";
import { CheckIcon, LoaderCircle, Search, Network, Star } from "lucide-react";
import { useUser } from "../../context/UserContext";
import Tooltip from "../Tooltip/Tooltip";

const WorkflowInput = ({ title = "Workflow" }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAllWorkflows, setShowAllWorkflows] = useState(false);
  const [isUpdatingFavorite, setIsUpdatingFavorite] = useState<string | null>(null);

  const {
    workflows = [],
    fetchWorkflows,
    selectedWorkflow,
    setSelectedWorkflow,
    loading,
    toggleFavorite,
  } = useWorkflow();

  const { userId } = useUser();

  // Function to load workflow list
  const loadWorkflowList = async () => {
    try {
      await fetchWorkflows();
    } catch (error) {
      console.error("Error loading workflows:", error);
    }
  };

  // Initial load of workflows list
  useEffect(() => {
    loadWorkflowList();
  }, [fetchWorkflows, userId]);

  // Add refresh when favorites change
  useEffect(() => {
    if (!showAllWorkflows) {
      loadWorkflowList();
    }
  }, [showAllWorkflows]);

  // Helper function to check if a workflow is favorited
  const isWorkflowFavorited = (workflowId: string) => {
    const workflow = workflows.find(w => w.id === workflowId);
    if (workflow?.favorites) {
      const favoriteValue = workflow.favorites[userId];
      if (typeof favoriteValue === 'object' && favoriteValue?.BOOL !== undefined) {
        return favoriteValue.BOOL === true;
      }
      return favoriteValue === true;
    }
    return false;
  };

  const getIconState = () => {
    if (selectedWorkflow) {
      return true;
    }
    return isHovered;
  };

  const handleWorkflowClick = (event: React.MouseEvent, workflow: any) => {
    event.preventDefault();
    if (selectedWorkflow?.id === workflow.id) {
      setSelectedWorkflow(null);
    } else {
      setSelectedWorkflow(workflow);
    }
  };

  const handleToggleFavorite = async (e: React.MouseEvent, workflowId: string) => {
    e.stopPropagation();

    if (isUpdatingFavorite === workflowId) {
      return;
    }

    try {
      setIsUpdatingFavorite(workflowId);

      await toggleFavorite(workflowId);

      // Refresh the list
      await loadWorkflowList();

    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsUpdatingFavorite(null);
    }
  };

  const handleViewToggle = async () => {
    const newShowAllWorkflows = !showAllWorkflows;
    setShowAllWorkflows(newShowAllWorkflows);
    await loadWorkflowList();
  };

  const filteredWorkflows = React.useMemo(() => {
    let filtered = workflows;

    // If there's a search query, filter by search regardless of view mode
    if (searchQuery) {
      filtered = workflows.filter(workflow =>
        workflow.workflow_name && workflow.workflow_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      return filtered;
    }

    // If not showing all workflows (showing favorites only)
    if (!showAllWorkflows) {
      filtered = workflows.filter(workflow => isWorkflowFavorited(workflow.id || ''));
    }

    return filtered;
  }, [workflows, showAllWorkflows, searchQuery, userId]);

  // Render star button with loading state
  const renderStarButton = (workflow: any) => {
    const isLoading = isUpdatingFavorite === workflow.id;
    const isFavorited = isWorkflowFavorited(workflow.id || '');

    return (
      <button
        onClick={(e) => handleToggleFavorite(e, workflow.id || '')}
        disabled={isLoading}
        className={`
          relative
          ${isFavorited
            ? "text-green-500"
            : "text-gray-400 hover:text-gray-600"}
          ${isLoading ? 'cursor-wait' : 'cursor-pointer'}
        `}
      >
        {isLoading ? (
          <LoaderCircle className="animate-spin" size={14} />
        ) : (
          <Star
            size={14}
            className={isFavorited ? "fill-current" : ""}
          />
        )}
      </button>
    );
  };

  return (
    <Popover className="relative">
      <Tooltip label={title} side="top">
        <Popover.Button
          className="bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary focus:outline-none transition-colors duration-200 p-2 rounded-full relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <span className="flex items-center">
            <Network size={18} strokeWidth={1.75} />
          </span>
        </Popover.Button>
      </Tooltip>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-150"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Popover.Panel className="absolute z-10 bg-surface-overlay text-primary ring-1 ring-border-default shadow-lg rounded-lg p-2 mt-3 w-[320px] max-h-[400px] overflow-hidden -right-21 -left-20 sm:-right-20">
          {loading ? (
            <div className="flex justify-center items-center p-4">
              <LoaderCircle className="animate-spin text-muted" size={17} />
            </div>
          ) : (
            <div className="space-y-2">
              {/* View toggle */}
              <button
                onClick={handleViewToggle}
                className={`w-full text-sm px-3 py-1.5 rounded-md flex items-center justify-between flex-shrink-0
                  ${showAllWorkflows
                    ? "text-muted hover:bg-surface-2"
                    : "text-primary bg-surface-2 hover:bg-surface-3"}`}
              >
                <span>{showAllWorkflows ? 'Showing all workflows' : 'Showing favorites'}</span>
                <Star
                  size={14}
                  className={!showAllWorkflows ? 'fill-current' : ''}
                />
              </button>

              {/* Search input */}
              <div className="relative flex-shrink-0">
                <input
                  type="search"
                  placeholder="Search workflows..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-1.5 pr-8 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                <Search size={14} className="absolute right-2.5 top-2 text-gray-400" />
              </div>

              {/* Workflow list */}
              <div className="max-h-[280px] overflow-y-auto pb-2">
                {filteredWorkflows.length > 0 ? (
                  filteredWorkflows.map((workflow) => (
                    <button
                      key={workflow.id}
                      onClick={(event) => handleWorkflowClick(event, workflow)}
                      className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-surface-2 transition group"
                    >
                      <span
                        className={`text-sm font-medium whitespace-nowrap text-left flex-grow pr-2
                          ${selectedWorkflow?.id === workflow.id ? "text-primary font-semibold" : "text-primary"}`}
                      >
                        {workflow.workflow_name || 'Unnamed Workflow'}
                      </span>
                      <div className="flex items-center space-x-2">
                        {renderStarButton(workflow)}
                        {selectedWorkflow?.id === workflow.id && (
                          <CheckIcon className="text-green-500 w-4 h-4" />
                        )}
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    {showAllWorkflows
                      ? searchQuery
                        ? "No workflows match your search"
                        : "No workflows available"
                      : searchQuery
                        ? "No workflows match your search"
                        : "No favorite workflows yet"}
                  </p>
                )}
              </div>
            </div>
          )}
        </Popover.Panel>
      </Transition>
    </Popover>
  );
};

export default WorkflowInput;
